import { Box, Typography } from '@mui/material';
import {
  ComponentProps,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClickAway } from 'react-use';

import { Markdown } from '@/components/Markdown/Markdown';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { TextEditor } from '@/components/TextEditor/TextEditor';
import {
  EditorInner,
  TextEditorWrapper as TextEditorWrapperExternal,
} from '@/components/TextEditor/TextEditor.utils';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { canonicalizeSummaryForDirtyCheck } from '@/modules/documents/documents.utils';
import { useVariableHighlight } from '@/modules/slideEditor/textEditors/hooks/useVariableHighlight';
import {
  SLIDE_EDITOR_HOVER,
  VARIABLE_HIGHLIGHT_HOVER_SX,
  VARIABLE_HIGHLIGHT_SX,
} from '@/modules/slideEditor/textEditors/SlideEditor.constants';
import { isClickInsideEditorPortal } from '@/modules/slideEditor/textEditors/utils/isClickInsideEditorPortal';
import { COLORS } from '@/styles/tokens/colors';

import { PRESENTATION_TYPE_STYLES } from './PresentationMarkdownField.constants';
import { PresentationMarkdownFieldToolbarPlugin } from './PresentationMarkdownField.toolbar';

/** Named presentation type styles the field can adopt.
 *  - `'titleS'` bolds the field at body size for the primary line of
 *    a header/body pair (e.g. a document name above its date).
 *  - `'titleM'` matches Figma "Presentation/TitleM" (16/24 semibold,
 *    gray-900) — used for card-level headers. Implicitly hides the
 *    Bold toolbar button since the type style forces semibold.
 *  - `'subtitle'` shifts the text color to gray-500 for secondary
 *    text under a heavier title; size/weight stay at the `default`
 *    baseline.
 *  - `'eyebrow'` matches Figma "Presentation/eyebrowBold" (12/18
 *    semibold, 1px letter-spacing, uppercase) and implicitly hides
 *    the Bold toolbar button since bold is part of the style — the
 *    button would otherwise appear interactive with no visible
 *    effect.
 *
 *  Add new variants to this union when they're introduced in
 *  `PRESENTATION_TYPE_STYLES`. */
export type PresentationMarkdownFieldVariant =
  | 'titleS'
  | 'titleM'
  | 'subtitle'
  | 'eyebrow';

export interface PresentationMarkdownFieldProps {
  /** Markdown content rendered in the read-only display and used as the
   *  initial value when entering edit mode. */
  content: string;
  /** Fired when the user clicks away from the editor. The argument is the
   *  latest markdown content; only fires when the content has actually
   *  changed (whitespace-canonicalized comparison).
   *
   *  When omitted, the field is non-interactive: no hover outline, no edit
   *  mode, and no empty-state placeholder. This is the mode used for
   *  print/export rendering, matching how `SlideEditorRichTextEditor`
   *  drops its empty state when no submit callback is provided. */
  onBlur?: (content: string) => void;
  /** Text rendered in place of the markdown when `content` is empty and
   *  the editor is not active. Defaults to "Click to add text".
   *  Ignored when `onBlur` is omitted (print/read-only mode). */
  placeholder?: string;
  /** When true, suppresses the blue hover outline on the read-only display.
   *  Use when the parent container already provides its own affordance. */
  disableHover?: boolean;
  /** Hide the Bold toolbar button. Useful for fields where bold is
   *  forced (e.g. eyebrow labels) — the button would otherwise appear
   *  interactive but have no visible effect. Automatically forced on
   *  by variants whose type style already implies bold (e.g.
   *  `'eyebrow'`); explicit consumers don't have to think about it. */
  hideBoldButton?: boolean;
  /** Predefined presentation type style applied to the field's text.
   *  See `PresentationMarkdownField.constants.ts` for the named specs.
   *  Encapsulates the styling AND any related toolbar behavior (e.g.
   *  hiding the Bold button for variants whose style already implies
   *  bold). */
  variant?: PresentationMarkdownFieldVariant;
}

/**
 * Reusable inline markdown editor for presentation slides.
 *
 * - Read-only by default; renders a blue outline on hover (configurable).
 * - Click to enter edit mode — mounts a `TextEditor` with a floating
 *   bold/italic/strikethrough toolbar above the content.
 * - Click-away exits edit mode and fires `onBlur` with the latest content
 *   (no-op when nothing changed).
 *
 * Differs from `SlideEditorRichTextEditor` by omitting block types, font
 * sizes, tables, prompt buttons, the variable typeahead/menu, and AI
 * streaming previews. `{Variable name}` tokens still decode to their
 * resolved values (with a hover-only chip in read-only mode and an
 * always-on chip in edit mode) — they just can't be inserted from a menu
 * here.
 */
export function PresentationMarkdownField({
  content,
  onBlur,
  placeholder = 'Click to add text',
  disableHover,
  hideBoldButton,
  variant,
}: PresentationMarkdownFieldProps) {
  // Targeted overrides on the inner paragraph. Spread onto each render
  // branch's `<Box>` so the markdown picks up the change in both
  // read-only and editing modes — without callers having to reach in
  // via `& p`. The `default` type style (Inter Regular 400) is always
  // applied as the baseline so the field matches the Figma body
  // weight even when no variant is passed; named variants layer on top.
  const paragraphSx: { '& p': Record<string, string | number> } = useMemo(
    () => ({
      '& p': {
        ...PRESENTATION_TYPE_STYLES.default,
        ...(variant ? PRESENTATION_TYPE_STYLES[variant] : {}),
      },
    }),
    [variant]
  );

  // Variants whose type style is intrinsically bold — keep this in
  // sync with `PRESENTATION_TYPE_STYLES`. Surfacing the Bold toolbar
  // button on these would be misleading: toggling it would have no
  // visible effect because the style forces semibold weight.
  const variantImpliesBold =
    variant === 'eyebrow' || variant === 'titleS' || variant === 'titleM';
  const effectiveHideBoldButton = hideBoldButton || variantImpliesBold;

  // Variable highlighting: decode `{Variable}` tokens to their resolved
  // display values in read-only mode and surface them as raw highlighted
  // tokens in edit mode. The variable typeahead/menu is intentionally NOT
  // wired up here — this field is for inline polish on slide content, not
  // template authoring.
  const {
    nodes: variableNodes,
    displayPlugins: variableDisplayPlugins,
    editPlugins: variableEditPlugins,
    resolverVersion,
  } = useVariableHighlight();

  const [isEditing, setIsEditing] = useState(false);
  // The field commits on click-away, so uncommitted edits live in the editor
  // while it's open. Block browser navigation for the duration of edit mode so
  // a back/forward or tab close mid-edit prompts instead of dropping the text.
  useOnBeforeUnloadPrompt(isEditing);
  // Latest content from the editor's onUpdate. Stored in a ref so toolbar
  // re-renders during editing don't churn this component.
  const latestContentRef = useRef(content);
  const editorRef = useRef<HTMLDivElement>(null);

  // Wrap the toolbar plugin so we can inject `hideBoldButton` (a
  // PresentationMarkdownField-level concern that `TextEditor` doesn't
  // know about). Memoize on the flag so the component identity stays
  // stable across re-renders of the field — without this the editor
  // would unmount and remount the toolbar plugin on every render.
  const ToolbarPlugin = useMemo(() => {
    const Wrapped = (
      props: ComponentProps<typeof PresentationMarkdownFieldToolbarPlugin>
    ) => (
      <PresentationMarkdownFieldToolbarPlugin
        {...props}
        hideBoldButton={effectiveHideBoldButton}
      />
    );
    Wrapped.displayName = 'BoundPresentationMarkdownFieldToolbarPlugin';
    return Wrapped;
  }, [effectiveHideBoldButton]);

  // Bridges the gap between the editor exiting edit mode and the parent
  // re-rendering with the new `content` prop. Without it, the read-only
  // `Markdown` view briefly renders the OLD `content` (since the parent
  // hasn't yet processed `onBlur`) before flipping to the new value —
  // a one-frame flash of stale text. While set, the read-only and
  // editing branches both use this value as their content; the effect
  // below clears it once the prop catches up.
  const [committedContent, setCommittedContent] = useState<string | null>(null);
  const displayContent = committedContent ?? content;

  useEffect(() => {
    // Drop the local override once the parent's `content` prop matches
    // (or, more conservatively, anytime the prop changes — at that point
    // the parent is the source of truth again).
    if (committedContent !== null) {
      setCommittedContent(null);
    }
    // Intentionally only react to `content` so we don't immediately
    // clear the override the same render it's set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useClickAway(editorRef, (event) => {
    if (!isEditing) return;
    if (isClickInsideEditorPortal(event.target)) return;

    const next = latestContentRef.current;
    // The markdown roundtrip can rewrite whitespace without semantic change;
    // canonicalize before comparing so we don't fire onBlur for no-op edits.
    const normalizedNext = canonicalizeSummaryForDirtyCheck(next);
    const normalizedOriginal = canonicalizeSummaryForDirtyCheck(displayContent);
    if (normalizedNext !== normalizedOriginal) {
      // Pin the read-only view to the committed text immediately so it
      // doesn't fall back to the (still-stale) prop during the parent's
      // post-onBlur re-render.
      setCommittedContent(next);
      onBlur?.(next);
    }
    setIsEditing(false);
  });

  // No edit callback → render the Markdown as plain output. Skips the hover
  // outline and empty-state placeholder so print/export rendering matches
  // the slide's static appearance.
  if (!onBlur) {
    return (
      <TextEditorProvider theme="presentationContent">
        <Box
          data-presentation-field
          sx={[
            FIELD_PARAGRAPH_RESET_SX,
            paragraphSx,
            VARIABLE_HIGHLIGHT_HOVER_SX,
          ]}
        >
          <Markdown
            key={resolverVersion}
            markdown={content}
            EditorInner={FieldEditorInner}
            additionalNodes={variableNodes}
            additionalPlugins={variableDisplayPlugins}
          />
        </Box>
      </TextEditorProvider>
    );
  }

  if (isEditing) {
    return (
      // `presentationContent` removes the contentEditable's default padding
      // (15px 10px) and minHeight (150px), so the editor box hugs the text
      // and matches the read-only view's height exactly.
      <TextEditorProvider theme="presentationContent">
        <Box
          ref={editorRef}
          data-presentation-field
          sx={[
            {
              // Allow the floating toolbar's Popper to escape this container's flow.
              position: 'relative',
              // Match the read-only view's sizing so the field doesn't jump or
              // grow on click — the box always wraps its current text content.
              width: 'fit-content',
              // Persist the same blue outline that hover shows in read-only mode,
              // so the field stays visibly "active" while editing. Match the
              // hover outline's `borderRadius` (`SLIDE_EDITOR_HOVER`) so the
              // corners don't snap from rounded to square on entering edit
              // mode.
              outline: disableHover ? 'none' : `1px solid ${COLORS.BLUE[600]}`,
              outlineOffset: '3px',
              borderRadius: '1px',
              ...FIELD_PARAGRAPH_RESET_SX,
            },
            paragraphSx,
            VARIABLE_HIGHLIGHT_SX,
          ]}
        >
          <TextEditor
            initialValue={displayContent}
            initialValueKind="markdown"
            onImmediateUpdate={(str) => {
              latestContentRef.current = str;
            }}
            ToolbarPlugin={ToolbarPlugin}
            TextEditorWrapper={InlineTextEditorWrapper}
            EditorInner={FieldEditorInner}
            additionalNodes={variableNodes}
            additionalPlugins={variableEditPlugins}
          />
        </Box>
      </TextEditorProvider>
    );
  }

  return (
    <TextEditorProvider theme="presentationContent">
      <Box
        data-presentation-field
        onClick={() => {
          latestContentRef.current = displayContent;
          setIsEditing(true);
        }}
        sx={[
          ...(disableHover
            ? [{ width: '100%', height: '100%' }]
            : [SLIDE_EDITOR_HOVER]),
          { cursor: 'text' },
          FIELD_PARAGRAPH_RESET_SX,
          paragraphSx,
          VARIABLE_HIGHLIGHT_HOVER_SX,
        ]}
      >
        {displayContent ? (
          <Markdown
            key={resolverVersion}
            markdown={displayContent}
            EditorInner={FieldEditorInner}
            additionalNodes={variableNodes}
            additionalPlugins={variableDisplayPlugins}
          />
        ) : (
          <Typography variant="body-s" sx={{ color: COLORS.TEXT.PLACEHOLDER }}>
            {placeholder}
          </Typography>
        )}
      </Box>
    </TextEditorProvider>
  );
}

/** Field-level paragraph adjustments:
 *  - Strip the shared TextEditor `paddingBottom: 4px` (and any bottom
 *    margin from list themes) — those exist so consecutive paragraphs
 *    space apart in multi-line editors, but this field is single-line by
 *    intent and the bottom gap leaves an awkward strip between the text
 *    and the field's outline (visible on hover and while editing).
 *  - Add 4px of right padding so italic glyphs (whose right side bearings
 *    extend past the upright box) aren't clipped by the field's outline. */
const FIELD_PARAGRAPH_RESET_SX = {
  '& p': { paddingBottom: 0, marginBottom: 0, paddingRight: '4px' },
};

/** Shared inner used by both the read-only `Markdown` view and the
 *  editing `TextEditor` so the rendered text sits in identical wrappers
 *  across modes (no border radius, no opaque background). */
function FieldEditorInner({ children }: PropsWithChildren) {
  return (
    <EditorInner borderRadius={0} sx={{ background: 'transparent' }}>
      {children}
    </EditorInner>
  );
}

function InlineTextEditorWrapper({ children }: PropsWithChildren) {
  // height="auto" lets the wrapper size to its content; with the parent at
  // width: fit-content this keeps the editor box flush around the text.
  return (
    <TextEditorWrapperExternal height="auto" hideBorder>
      {children}
    </TextEditorWrapperExternal>
  );
}
